/**
 * Skills API
 *
 * RTK Query endpoints for skill catalog and progress tracking.
 */

import { api } from '../api';

// ============================================
// TYPES
// ============================================

export type DifficultyLevel = 'beginner' | 'intermediate' | 'advanced';

export interface SkillDomain {
  id: string;
  key: string;
  name: string;
  description: string;
  icon?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  categories?: SkillCategory[];
}

export interface SkillCategory {
  id: string;
  domainId: string;
  key: string;
  name: string;
  description: string;
  orderIndex: number;
  createdAt: string;
  updatedAt: string;
  objectives?: SkillObjective[];
  domain?: SkillDomain;
}

export interface SkillObjective {
  id: string;
  categoryId: string;
  key: string;
  objectiveText: string;
  difficulty: DifficultyLevel;
  requiresSpokenAssessment: boolean;
  requiresKnowledgeAssessment: boolean;
  createdAt: string;
  updatedAt: string;
  category?: SkillCategory;
}

export interface UserSkillProgress {
  id: string;
  userId: string;
  objectiveId: string;
  knowledgeScore?: number;
  deliveryScore?: number;
  authorityScore?: number;
  attemptsCount: number;
  lastAttemptAt?: string;
  passed: boolean;
  createdAt: string;
  updatedAt: string;
  objective?: SkillObjective;
}

export interface DomainProgress {
  domainId: string;
  domainName: string;
  domainKey: string;
  totalObjectives: number;
  attemptedCount: number;
  passedCount: number;
  averageAuthorityScore?: number;
}

export interface ProgressSummary {
  totalObjectivesAttempted: number;
  totalObjectivesPassed: number;
  /**
   * False while no evidence-based evaluator exists (RC19): passes and averages
   * are NOT SCORED (rendered "Not scored", never 0). Absent from an older server.
   */
  scoringAvailable?: boolean;
  averageAuthorityScore?: number;
  progressByDomain: DomainProgress[];
}

export interface CategoryProgress {
  categoryId: string;
  categoryName: string;
  categoryKey: string;
  totalObjectives: number;
  attemptedCount: number;
  passedCount: number;
  objectives: ObjectiveProgress[];
}

export interface ObjectiveProgress {
  objectiveId: string;
  objectiveKey: string;
  objectiveText: string;
  difficulty: DifficultyLevel;
  progress?: UserSkillProgress;
}

export interface CatalogStats {
  totalDomains: number;
  activeDomains: number;
  totalCategories: number;
  totalObjectives: number;
  objectivesByDifficulty: Record<DifficultyLevel, number>;
}

/** One public row of an objective leaderboard (opt-in users only; no email or user id). */
export interface LeaderboardEntry {
  rank: number;
  name: string;
  publicSlug: string | null;
  authorityScore: number;
  passed: boolean;
}

// Request types
export interface CreateDomainRequest {
  key: string;
  name: string;
  description: string;
  icon?: string;
  isActive?: boolean;
}

export interface UpdateDomainRequest {
  name?: string;
  description?: string;
  icon?: string;
  isActive?: boolean;
}

export interface CreateCategoryRequest {
  key: string;
  name: string;
  description: string;
  orderIndex?: number;
}

export interface UpdateCategoryRequest {
  name?: string;
  description?: string;
  orderIndex?: number;
}

export interface CreateObjectiveRequest {
  key: string;
  objectiveText: string;
  difficulty?: DifficultyLevel;
  requiresSpokenAssessment?: boolean;
  requiresKnowledgeAssessment?: boolean;
}

export interface UpdateObjectiveRequest {
  objectiveText?: string;
  difficulty?: DifficultyLevel;
  requiresSpokenAssessment?: boolean;
  requiresKnowledgeAssessment?: boolean;
}

// API Response wrapper
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// ============================================
// API ENDPOINTS
// ============================================

export const skillsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // ----------------------------------------
    // CATALOG ENDPOINTS
    // ----------------------------------------

    // Get full catalog (domains -> categories -> objectives)
    getFullCatalog: builder.query<SkillDomain[], { includeInactive?: boolean } | void>({
      query: (params) => ({
        url: '/skills/catalog',
        params: params ? { includeInactive: params.includeInactive } : undefined,
      }),
      transformResponse: (response: ApiResponse<SkillDomain[]>) => response.data,
      providesTags: ['SkillCatalog'],
    }),

    // Get catalog statistics
    getCatalogStats: builder.query<CatalogStats, void>({
      query: () => '/skills/catalog/stats',
      transformResponse: (response: ApiResponse<CatalogStats>) => response.data,
    }),

    // Get objective leaderboard
    getObjectiveLeaderboard: builder.query<LeaderboardEntry[], { objectiveId: string; limit?: number }>({
      query: ({ objectiveId, limit }) => ({
        url: `/skills/objectives/${objectiveId}/leaderboard`,
        params: { limit },
      }),
      transformResponse: (response: ApiResponse<LeaderboardEntry[]>) => response.data,
    }),

    // ----------------------------------------
    // PROGRESS ENDPOINTS
    // ----------------------------------------

    // Get my progress summary
    getMyProgress: builder.query<ProgressSummary, void>({
      query: () => '/skills/progress',
      transformResponse: (response: ApiResponse<ProgressSummary>) => response.data,
      providesTags: ['SkillProgress'],
    }),

    // Get my progress in one category (per-objective completion on /skills)
    getCategoryProgress: builder.query<CategoryProgress, string>({
      query: (categoryId) => `/skills/progress/category/${categoryId}`,
      transformResponse: (response: ApiResponse<CategoryProgress>) => response.data,
      providesTags: (_result, _error, categoryId) => [{ type: 'SkillProgress', id: categoryId }],
    }),
  }),
  overrideExisting: false,
});

// ============================================
// EXPORTED HOOKS
// ============================================

export const {
  // Catalog
  useGetFullCatalogQuery,
  useGetCatalogStatsQuery,
  // Objectives
  useGetObjectiveLeaderboardQuery,
  // Progress
  useGetMyProgressQuery,
  useGetCategoryProgressQuery,
} = skillsApi;
